import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronDown, HelpCircle, MessageCircle, Phone, X } from 'lucide-react';
import { FAQS, WHATSAPP_LINK, CONTACT_PHONE } from '../constants';

const TALLY_EMBED_URL = 'https://tally.so/embed/dWa5xD?alignLeft=1&hideTitle=1&transparentBackground=1&dynamicHeight=1';

const FAQItem: React.FC<{ question: string; answer: string; index: number }> = ({
  question,
  answer,
  index,
}) => {
  const [isOpen, setIsOpen] = useState(index === 0);

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ delay: index * 0.05 }}
      className="border border-slate-200/60 rounded-2xl overflow-hidden bg-white hover:border-[#006d3d]/30 transition-all shadow-sm"
    >
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full px-6 py-5 flex items-center justify-between text-left focus:outline-none"
      >
        <span className="font-bold text-lg text-slate-900">{question}</span>
        <ChevronDown
          size={24}
          className={`text-slate-400 transition-transform duration-300 ${isOpen ? 'rotate-180 text-primary' : ''}`}
        />
      </button>
      <AnimatePresence initial={false}>
        {isOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3, ease: 'easeInOut' }}
            className="overflow-hidden"
          >
            <div className="px-6 pb-5 text-slate-600 leading-relaxed">
              {answer}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

const ContactModal: React.FC<{ onClose: () => void }> = ({ onClose }) => (
  <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 sm:p-6 md:p-10">
    <div
      className="absolute inset-0 bg-slate-900/80 backdrop-blur-sm transition-opacity animate-in fade-in duration-300"
      onClick={onClose}
    />
    <div className="relative w-full max-w-4xl max-h-full bg-white rounded-3xl shadow-2xl flex flex-col overflow-hidden animate-in zoom-in-95 duration-300">
      <div className="flex items-center justify-between p-6 border-b border-slate-100 bg-white sticky top-0 z-10">
        <h2 className="text-2xl font-bold text-primary flex items-center gap-2">
          <span className="w-2 h-8 bg-secondary rounded-full" />
          Cuéntanos sobre tu negocio
        </h2>
        <button
          onClick={onClose}
          className="p-2 rounded-full hover:bg-slate-100 text-slate-500 transition-colors"
          aria-label="Cerrar modal"
        >
          <X size={24} />
        </button>
      </div>
      <div className="flex-1 overflow-y-auto bg-white p-2">
        <div className="w-full">
          <iframe
            src={TALLY_EMBED_URL}
            loading="lazy"
            width="100%"
            height="681"
            frameBorder="0"
            marginHeight={0}
            marginWidth={0}
            title="Cuéntanos sobre tu negocio"
            className="w-full min-h-[319px]"
          />
        </div>
      </div>
    </div>
  </div>
);

export const FAQSection: React.FC = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);

  return (
    <section
      id="contacto"
      className="py-24"
      style={{ background: 'linear-gradient(#f8fafc 0%, #f1f5f9 100%)' }}
    >
      <div className="container mx-auto px-4 max-w-7xl">
        <div className="grid lg:grid-cols-2 gap-12">
          {/* FAQ Column */}
          <div id="faq" className="flex flex-col justify-center">
            <div className="relative z-10">
              <div className="mb-10">
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#eaedff] text-[#131b2e] font-bold text-sm uppercase tracking-wider mb-4"
                >
                  <HelpCircle size={18} />
                  Preguntas Frecuentes
                </motion.div>
                <motion.h2
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.1 }}
                  className="text-3xl md:text-4xl font-black text-slate-900 mb-4 font-display tracking-tight"
                >
                  Preguntas frecuentes
                </motion.h2>
              </div>
              <div className="space-y-4">
                {FAQS.map((faq, idx) => (
                  <FAQItem
                    key={idx}
                    question={faq.question}
                    answer={faq.answer}
                    index={idx}
                  />
                ))}
              </div>
            </div>
          </div>

          {/* Contact CTA Column */}
          <div
            className="p-8 md:p-10 rounded-2xl flex flex-col justify-center relative overflow-hidden"
            style={{
              background: '#ffffff',
              border: '1px solid rgba(0,0,0,0.07)',
              boxShadow: '0 8px 40px rgba(0,0,0,0.08)',
            }}
          >
            <div
              className="absolute top-0 left-0 w-full h-1 rounded-t-2xl"
              style={{ background: 'linear-gradient(90deg, #3CB371, #22d3ee)' }}
            />
            <span
              className="text-xs font-black uppercase tracking-widest mb-3"
              style={{ color: '#3CB371' }}
            >
              Hablemos
            </span>

            <div className="relative rounded-2xl overflow-hidden mb-8">
              <img
                src="/persona-grupo-reunion.jpg"
                alt=""
                className="w-full h-[180px] md:h-[260px] object-cover"
                style={{ objectPosition: '50% 35%' }}
                loading="lazy"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#f8fafc] via-[#f8fafc]/70 to-[#f8fafc]/40" />
              <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-4">
                <h2 className="text-2xl md:text-3xl font-black text-slate-900 mb-2 tracking-tight leading-tight">
                  Agenda tu consulta gratuita
                </h2>
              </div>
            </div>

            <p className="text-slate-600 mb-8 leading-relaxed">
              Descubre cuánto puedes ahorrar en una llamada de 15 minutos. Sin compromiso.
            </p>

            <button
              onClick={() => setIsModalOpen(true)}
              className="inline-flex items-center justify-center px-6 py-3 text-base font-medium rounded-lg transition-all duration-200 transform hover:-translate-y-0.5 focus:outline-none focus:ring-2 focus:ring-offset-2 bg-primary text-white hover:opacity-90 shadow-lg shadow-primary/30 focus:ring-primary w-full justify-center py-4 rounded-xl text-lg font-bold shadow-primary/20"
            >
              Solicitar consulta gratuita
            </button>

            <div className="grid grid-cols-2 gap-3 mt-3">
              <a
                href={`tel:${CONTACT_PHONE.replace(/\s/g, '')}`}
                className="flex-1 py-3 rounded-xl font-bold text-center flex items-center justify-center gap-2 transition-all duration-200"
                style={{
                  background: 'rgba(0,0,0,0.04)',
                  color: '#1e293b',
                  border: '1px solid rgba(0,0,0,0.07)',
                }}
              >
                <Phone size={18} />
                Llamar
              </a>
              <a
                href={WHATSAPP_LINK}
                target="_blank"
                rel="noreferrer"
                className="flex-1 text-white py-3 rounded-xl font-bold text-center flex items-center justify-center gap-2 transition-all duration-200 hover:opacity-90"
                style={{
                  background: 'linear-gradient(135deg, #3CB371, #2d8653)',
                  boxShadow: '0 4px 15px rgba(60,179,113,0.3)',
                }}
              >
                <MessageCircle size={18} />
                WhatsApp
              </a>
            </div>

            <div className="text-slate-400 text-sm mt-6 text-center">
              Garantía de devolución 30 días. Sin permanencias.
            </div>
          </div>
        </div>
      </div>

      {isModalOpen && <ContactModal onClose={() => setIsModalOpen(false)} />}
    </section>
  );
};
